import { decodeMeetingToken, MeetingRoom } from '../pages/meeting-view/api'
import { getSessionParameters, updateSessionParams } from '../helpers/urls'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'

import { getMeetingData } from '../apis/api-hca'
import { useCallback, useRef, useState } from 'react'
import { useUI } from '../context/UIContext'
import { useTranslation } from 'react-i18next'
import ToastNotification from '../pages/meeting-view/components/Misc/ToastNotification'
import { LinkBtnNotification } from '../components/Buttons'
import { setSessionItem } from '../helpers/localStorage'
import { useZoomContext } from '../context/ZoomContext'
import { logToSentry } from '../helpers/sentry'

export const useChangeMeeting = () => {
    const { showToast } = useUI()
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const { t } = useTranslation('notifications')
    const { timer } = useZoomContext()
    const [roomToSwitch, setRoomToSwitch] = useState<MeetingRoom | null>(null)
    const isNavigating = useRef(false)

    const changeMeeting = useCallback(
        async (newRoom: MeetingRoom, { immediate = false }: { immediate?: boolean } = {}) => {
            if (isNavigating.current) {
                console.log(`Blocked overlapping changeMeeting: ${newRoom}`)
                return
            }
            isNavigating.current = true
            console.log('Runs changeMeeting')
            queryClient.invalidateQueries({ queryKey: ['MeetingData'] })

            let data
            const maxRetries = 1 // We'll retry once
            let attempt = 0

            const fetchMeetingDataWithRetry = async () => {
                while (attempt <= maxRetries) {
                    try {
                        data = await getMeetingData(getSessionParameters()._algorithmId)
                        return true // Success, exit the loop
                    } catch (error: unknown) {
                        const { role } = getSessionParameters()
                        const errorMessage =
                            error instanceof Error ? error.message : 'Error fetching meeting data on useChangeMeeting'
                        const errorName = error instanceof Error ? error.name : 'Error changing meeting'
                        const status =
                            error instanceof Error && 'status' in error ? String((error as any).status) : 'unknown'

                        // Log each attempt failure to Sentry
                        logToSentry(
                            `Attempt ${
                                attempt + 1
                            } failed to fetch meeting data for room: ${newRoom}, user role: ${role}`,
                            'meeting',
                            {
                                error: errorMessage,
                                errorName: errorName,
                                status: status,
                            }
                        )

                        attempt++
                        if (attempt > maxRetries) {
                            // After max retries, throw the error to trigger the notification
                            throw error
                        }
                        // Optionally, add a small delay before retrying (e.g., 1 second)
                        await new Promise(resolve => setTimeout(resolve, 1000))
                    } finally {
                        isNavigating.current = false
                    }
                }
                return false // This line won’t be reached due to the throw, but included for completeness
            }

            try {
                await fetchMeetingDataWithRetry()
            } catch (error: unknown) {
                const { role } = getSessionParameters()
                const errorMessage =
                    error instanceof Error ? error.message : 'Error fetching meeting data after retries'
                const errorName = error instanceof Error ? error.name : 'Error changing meeting'
                const status = error instanceof Error && 'status' in error ? String((error as any).status) : 'unknown'

                // Log the final failure after retries to Sentry
                logToSentry(
                    `All attempts failed to fetch meeting data for room: ${newRoom}, user role: ${role}`,
                    'meeting',
                    {
                        error: errorMessage,
                        errorName: errorName,
                        status: status,
                    }
                )

                // Show the error toast with a retry option after all retries fail
                showToast.error(
                    <ToastNotification>
                        <div>
                            {t('error.fetchMeetingDataError')}
                            <LinkBtnNotification onClick={() => changeMeeting(newRoom)}>
                                {t('switchMeeting.retry')}
                            </LinkBtnNotification>
                        </div>
                    </ToastNotification>,
                    { duration: Infinity, id: 'fetch-meeting-data-error' }
                )
                return // Exit the function if fetch fails after retries
            }

            // Check if data exists before accessing it
            const meeting = data?.[`${newRoom}_room`]
            const errorNotification = (
                <ToastNotification>
                    <div>
                        {t('error.switchMeetError')}
                        <LinkBtnNotification onClick={() => changeMeeting(roomToSwitch || 'general')}>
                            {t('switchMeeting.goNow')}
                        </LinkBtnNotification>
                    </div>
                </ToastNotification>
            )

            if (!meeting) return showToast.error(errorNotification, { duration: Infinity, id: 'switch-meeting-error' })

            const { token, url } = meeting as { token: string; url: string }
            if (!token && !url)
                return showToast.error(errorNotification, { duration: Infinity, id: 'switch-meeting-error' })

            updateSessionParams({ room: newRoom })
            if (token) {
                const { room, tpc: topic } = decodeMeetingToken(token)
                updateSessionParams({ room, topic })
            }

            queryClient.invalidateQueries({ queryKey: ['Team', 'Timeline'] })
            showToast.dismiss('switch-meeting-error')

            if (url?.startsWith(window.location.origin) || url?.startsWith('https://develop.'))
                navigate('/post-meeting/moving')
            else {
                navigate('/post-meeting/external')
                window.open(url, 'external-meeting')
            }
        },
        [navigate, queryClient, roomToSwitch, showToast, t]
    )

    const manuallySwitch = (room: MeetingRoom) => {
        clearTimeout(timer.current)
        setSessionItem('manually-switched', true)
        changeMeeting(room)
    }

    //@ts-ignore
    window.changeMeeting = changeMeeting

    return { changeMeeting, manuallySwitch, setRoomToSwitch }
}
